import { createClient as createSupabaseClient } from '@supabase/supabase-js'
import { Database } from '@/types/supabase'

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL as string
const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY as string

// Token 刷新鎖機制（防止多個實例同時刷新 token）
let refreshTokenLock: Promise<void> | null = null
let refreshTokenLockExpiry: number = 0
const REFRESH_LOCK_DURATION = 5000 // 5秒鎖定時間

/**
 * 獲取刷新鎖，防止多個實例同時刷新 token
 */
async function acquireRefreshLock(): Promise<() => void> {
  // 如果鎖已過期，清除它
  if (refreshTokenLock && Date.now() > refreshTokenLockExpiry) {
    refreshTokenLock = null
  }

  // 如果有活躍的鎖，等待它完成
  if (refreshTokenLock) {
    await refreshTokenLock
  }

  // 創建新的鎖
  let releaseLock: () => void
  refreshTokenLock = new Promise((resolve) => {
    releaseLock = resolve
  })
  refreshTokenLockExpiry = Date.now() + REFRESH_LOCK_DURATION

  return () => {
    releaseLock()
    // 延遲清除鎖，確保其他等待的請求能獲取到
    setTimeout(() => {
      if (Date.now() > refreshTokenLockExpiry) {
        refreshTokenLock = null
      }
    }, 100)
  }
}

// 創建 Supabase 客戶端的函數
function createSupabaseClientInstance() {
  // console.log('創建 Supabase 客戶端實例', {
  //   url: supabaseUrl,
  //   anonKeyProvided: !!supabaseKey
  // })

  const client = createSupabaseClient<Database>(supabaseUrl, supabaseKey, {
    auth: {
      persistSession: true,
      storageKey: 'supabase.auth.token',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      detectSessionInUrl: true,
      flowType: 'implicit',  // 改用 implicit flow，避免 PKCE 問題
      autoRefreshToken: true,
      // 禁用 token 輪換，避免 "Already Used" 錯誤
      // @ts-ignore - refreshTokenRotationEnabled 可能在某些 Supabase SDK 版本中不存在
      refreshTokenRotationEnabled: false
    },
    global: {
      headers: {
        'x-client-info': 'jlpt-mvp/1.0.0'
      }
    }
  })

  // 攔截 token 刷新請求，添加鎖機制
  if (typeof window !== 'undefined') {
    const originalRefreshSession = client.auth.refreshSession.bind(client.auth)
    
    // @ts-ignore - refreshSession 的參數類型在不同 Supabase SDK 版本中可能不同
    client.auth.refreshSession = async (refreshToken?: string | { refresh_token: string }) => {
      const releaseLock = await acquireRefreshLock()
      try {
        // @ts-ignore - 類型兼容性問題
        const result = await originalRefreshSession(refreshToken)
        return result
      } catch (error: any) {
        // 如果是 "Already Used" 錯誤，嘗試重新獲取 session
        if (error?.message?.includes('Already Used') || error?.message?.includes('Invalid Refresh Token')) {
          // 等待一小段時間後重試（可能其他實例已經刷新成功）
          await new Promise(resolve => setTimeout(resolve, 100))
          // 嘗試獲取當前 session（可能已被其他實例更新）
          const { data: { session } } = await client.auth.getSession()
          if (session) {
            // 如果已有有效 session，返回它
            return { data: { session, user: session.user }, error: null }
          }
        }
        throw error
      } finally {
        releaseLock()
      }
    }
  }

  // 添加調試事件監聽器
  if (typeof window !== 'undefined') {
    client.auth.onAuthStateChange((event, session) => {
      if (process.env.NODE_ENV === 'development') {
        // 認證狀態變更時的處理
      }
      
      // 將 session 同步到 cookies，讓 middleware 可以讀取
      if (event === 'SIGNED_IN' && session) {
        const cookieName = `sb-${supabaseUrl?.split('//')[1]?.split('.')[0]}-auth-token`
        const maxAge = 100 * 365 * 24 * 60 * 60 // 100 years
        // 🔧 修復：統一使用 www.jlpt-aitutor.com domain
        const isLocalhost = window.location.hostname === 'localhost'
        const isProduction = window.location.hostname.includes('jlpt-aitutor.com')
        const domain = isLocalhost ? '' : (isProduction ? '.jlpt-aitutor.com' : '')
        const domainPart = domain ? `; domain=${domain}` : ''
        document.cookie = `${cookieName}=${JSON.stringify(session)}; path=/; max-age=${maxAge}; SameSite=Lax; Secure${domainPart}`
        // console.log(`[AUTH] Session synced to cookie with domain: ${domain || 'no domain'}`)
      } else if (event === 'SIGNED_OUT') {
        const cookieName = `sb-${supabaseUrl?.split('//')[1]?.split('.')[0]}-auth-token`
        const isLocalhost = window.location.hostname === 'localhost'
        const isProduction = window.location.hostname.includes('jlpt-aitutor.com')
        const domain = isLocalhost ? '' : (isProduction ? '.jlpt-aitutor.com' : '')
        const domainPart = domain ? `; domain=${domain}` : ''
        document.cookie = `${cookieName}=; path=/; max-age=0${domainPart}`
        // console.log(`[AUTH] Session cookie cleared with domain: ${domain || 'no domain'}`)
      }
    })
  }

  return client
}

// 單例模式管理 Supabase 客戶端
class SupabaseClientSingleton {
  private static instance: ReturnType<typeof createSupabaseClient<Database>> | null = null

  /**
   * 獲取 Supabase 客戶端實例
   * 保證永遠返回非 null 的實例（如果不存在則創建）
   */
  public static getInstance(): ReturnType<typeof createSupabaseClient<Database>> {
    if (!this.instance) {
      // 創建唯一客戶端實例
      // @ts-ignore - 複雜的泛型類型推斷問題，實際運行時類型正確
      this.instance = createSupabaseClientInstance()
    }
    // 使用非空斷言，因為邏輯上保證不會為 null
    // 如果 instance 為 null，上面的 if 會創建新實例
    return this.instance!
  }
}

// 導出單一全局實例（保證非 null）
// @ts-ignore - 複雜的泛型類型推斷問題，實際運行時類型正確
export const supabase: ReturnType<typeof createSupabaseClient<Database>> = SupabaseClientSingleton.getInstance()

// 導出創建客戶端的函數（用於兼容性）
export const getSupabase = () => supabase

// 僅在瀏覽器環境執行的初始化邏輯
if (typeof window !== 'undefined') {
  const initializeSupabaseDebug = () => {
    try {
      const supabaseInstance = SupabaseClientSingleton.getInstance()
      
      // 創建全局調試方法
      const debugSupabase = {
        getInstance: () => supabaseInstance,
        getUser: () => supabaseInstance.auth.getUser(),
        getSession: () => supabaseInstance.auth.getSession(),
        printUserInfo: async () => {
          // console.log('開始執行 printUserInfo 方法')
          try {
            // console.log('檢查本地存儲:', {
            //   hasAuthToken: !!localStorage.getItem('supabase.auth.token'),
            //   hasMockSession: !!localStorage.getItem('mock_session')
            // })

            // console.log('嘗試獲取用戶信息...')
            
            // 增加超時機制
            const userPromise = supabaseInstance.auth.getUser()
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('用戶信息獲取超時')), 5000)
            )

            const { data, error } = await Promise.race([userPromise, timeoutPromise]) as any
            
            if (process.env.NODE_ENV === 'development') {
              console.group('用戶信息調試')
              // console.log('登入狀態:', !!data?.user)
              // console.log('Email:', data?.user?.email)
              
              if (!data?.user) {
                console.warn('未找到用戶，可能未登錄')
              }
              
              console.groupEnd()
            }

            // 額外的會話檢查
            const { data: sessionData, error: sessionError } = await supabaseInstance.auth.getSession()
            if (process.env.NODE_ENV === 'development') {
              console.group('會話信息調試')
              // console.log('會話狀態:', !!sessionData?.session)
              // console.log('用戶已登入:', !!sessionData?.session?.user)
              if (sessionError) {
                console.warn('會話錯誤:', sessionError.message)
              }
              console.groupEnd()
            }

            return { 
              userData: data, 
              sessionData,
              localStorageAuth: localStorage.getItem('supabase.auth.token')
            }
          } catch (err) {
            console.error('獲取用戶信息時發生異常:', err)
            throw err
          }
        },
        // 手動重新登錄方法
        manualLogin: async () => {
          try {
            // console.log('嘗試手動登錄...')
            const { data, error } = await supabaseInstance.auth.signInWithOAuth({
              provider: 'google',
              options: {
                redirectTo: window.location.origin
              }
            })
            // console.log('手動登錄結果:', { data, error })
            return { data, error }
          } catch (err) {
            console.error('手動登錄失敗:', err)
            throw err
          }
        },
        // 診斷方法
        diagnose: async () => {
          if (process.env.NODE_ENV === 'development') {
            console.group('Supabase 診斷')
            // console.log('環境配置:', {
            //   urlConfigured: !!supabaseUrl,
            //   anonKeyConfigured: !!supabaseKey
            // })
            
            try {
              const session = await supabaseInstance.auth.getSession()
              // console.log('會話狀態:', {
              //   hasSession: !!session.data.session,
              //   isLoggedIn: !!session.data.session?.user
              // })
            } catch (err) {
              console.warn('會話檢查失敗')
            }

            try {
              const user = await supabaseInstance.auth.getUser()
              // console.log('用戶狀態:', {
              //   isAuthenticated: !!user.data.user,
              //   email: user.data.user?.email
              // })
            } catch (err) {
              console.warn('用戶檢查失敗')
            }

            // console.log('本地存儲狀態:', {
            //   hasAuthToken: !!localStorage.getItem('supabase.auth.token'),
            //   hasMockSession: !!localStorage.getItem('mock_session')
            // })

            console.groupEnd()
          }
        }
      }

      // 掛載到 window
      Object.defineProperties(window, {
        debugSupabase: { 
          value: debugSupabase,
          configurable: true,
          writable: true
        },
        _supabaseInstance: { 
          value: supabaseInstance,
          configurable: true,
          writable: true
        }
      })
      
      // 調試方法已掛載
    } catch (error) {
      console.error('初始化 Supabase 調試方法失敗:', error)
    }
  }

  // 確保在客戶端完全加載後執行
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeSupabaseDebug)
  } else {
    initializeSupabaseDebug()
  }
}
